import React, { useState } from 'react';
import { Form, Field, FormRenderProps } from 'react-final-form';
import { makeStyles, Theme } from '@material-ui/core/styles';
import {
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Collapse,
    Divider,
    IconButton,
    TextField,
    Typography,
} from '@material-ui/core';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import LocalOfferOutlinedIcon from '@material-ui/icons/LocalOfferOutlined';
import CheckIcon from '@material-ui/icons/Check';
import RefreshIcon from '@material-ui/icons/Refresh';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { client } from '../..';
import { showNotification } from '../../App';
import { updateApplicationTerminologyLocalStorage } from '../../helpers/commonHelper';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

interface TerminologyFormValues {
    singularLabel: string | null;
    pluralLabel: string | null;
    helperText: string | null;
    tooltip: string | null;
}

/* -------------------------------------------------------------------------- */
/* Styles                                                                     */
/* -------------------------------------------------------------------------- */

const useStyles = makeStyles((theme: Theme) => ({
    card: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        marginBottom: theme.spacing(2.5),
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        padding: theme.spacing(2, 2.5),
        backgroundColor: '#fafafa',
        cursor: 'pointer',
    },
    headerBody: {
        flex: 1,
        minWidth: 0,
        marginLeft: theme.spacing(2),
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
    },
    title: {
        fontWeight: 600,
        marginRight: theme.spacing(1),
    },
    keyChip: {
        fontFamily: '"Roboto Mono", Consolas, monospace',
        fontSize: '0.75rem',
        height: 22,
        backgroundColor: '#eceff1',
        color: theme.palette.text.secondary,
    },
    description: {
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    previewRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
    },
    previewChip: {
        backgroundColor: '#eceff1',
        fontWeight: 400,
    },
    previewChipStrong: {
        fontWeight: 600,
    },
    infoChip: {
        backgroundColor: 'transparent',
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.secondary,
    },
    avatar: {
        backgroundColor: '#e8f5e9',
        color: '#43a047',
        border: '1px solid #c8e6c9',
    },
    expandButton: {
        marginLeft: theme.spacing(1),
    },
    content: {
        padding: theme.spacing(3, 2.5),
    },
    sectionLabel: {
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.text.secondary,
        letterSpacing: 0.5,
        fontSize: '0.75rem',
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        '& svg': {
            fontSize: '1rem',
            marginRight: theme.spacing(1),
        },
    },
    fieldsRow: {
        display: 'flex',
        gap: theme.spacing(4),
        flexWrap: 'wrap',
    },
    fieldCol: {
        flex: '1 1 260px',
    },
    fieldHeading: {
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: 0.5,
        marginBottom: theme.spacing(1),
    },
    defaultHint: {
        marginTop: theme.spacing(0.75),
        color: theme.palette.text.disabled,
        fontStyle: 'italic',
        fontSize: '0.8rem',
    },
    divider: {
        margin: theme.spacing(3, 0),
    },
    helperHint: {
        marginTop: theme.spacing(0.75),
        color: theme.palette.text.secondary,
        fontSize: '0.8rem',
    },
    actionsRow: {
        display: 'flex',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(3),
    },
    saveButton: {
        borderColor: theme.palette.text.primary,
        color: theme.palette.text.primary,
    },
}));

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function humanizeLabelKey(labelKey: string): string {
    return labelKey
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase());
}

// const required = (value?: string) => (value && value.trim().length > 0 ? undefined : 'Required');

/* -------------------------------------------------------------------------- */
/* TerminologyCard                                                            */
/* -------------------------------------------------------------------------- */

interface TerminologyCardProps {
    item: ApplicationTerminology;
    defaultExpanded?: boolean;
    onSave: (values: TerminologyFormValues) => Promise<void>;
    onResetToDefault: () => void;
}

export function TerminologyCard(props: TerminologyCardProps) {
    const { item, defaultExpanded = false, onSave, onResetToDefault } = props;
    const classes = useStyles();

    const [expanded, setExpanded] = useState(defaultExpanded);

    const displayTitle = item.internalDisplayLabel || humanizeLabelKey(item.labelKey);

    const handleSubmit = async (
        values: TerminologyFormValues,
        form: FormRenderProps<TerminologyFormValues>['form'],
    ) => {
        await onSave(values);
        form.reset(values);
    };

    const handleResetToDefault = (form: FormRenderProps<TerminologyFormValues>['form']) => {
        onResetToDefault();
        form.reset({
            singularLabel: null,
            pluralLabel: null,
            helperText: null,
            tooltip: '',
        });
    };

    return (
        <Card className={classes.card} elevation={0}>
            <div className={classes.header} onClick={() => setExpanded((prev) => !prev)}>
                <Avatar className={classes.avatar}>
                    <StarBorderIcon />
                </Avatar>

                <div className={classes.headerBody}>
                    <div className={classes.titleRow}>
                        <Typography variant="h6" className={classes.title}>
                            {displayTitle}
                        </Typography>
                        <Chip size="small" label={item.labelKey} className={classes.keyChip} />
                    </div>

                    {item.helperText && (
                        <Typography variant="body2" className={classes.description}>
                            {item.helperText}
                        </Typography>
                    )}

                    <div className={classes.previewRow}>
                        <Chip
                            size="small"
                            className={classes.previewChip}
                            label={
                                <span>
                                    Singular: <span className={classes.previewChipStrong}>{item.singularLabel}</span>
                                </span>
                            }
                        />
                        <Chip
                            size="small"
                            className={classes.previewChip}
                            label={
                                <span>
                                    Plural: <span className={classes.previewChipStrong}>{item.pluralLabel}</span>
                                </span>
                            }
                        />
                        <Chip
                            size="small"
                            icon={<InfoOutlinedIcon fontSize="small" />}
                            className={classes.infoChip}
                            label={item.tooltip ? 'Tooltip set' : 'No tooltip set'}
                        />
                    </div>
                </div>

                <IconButton
                    size="small"
                    className={classes.expandButton}
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded((prev) => !prev);
                    }}
                >
                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
            </div>

            <Collapse in={expanded} unmountOnExit>
                <CardContent className={classes.content}>
                    <Form<TerminologyFormValues>
                        onSubmit={handleSubmit}
                        initialValues={{
                            singularLabel: item.singularLabel,
                            pluralLabel: item.pluralLabel,
                            helperText: item.helperText,
                            tooltip: item.tooltip || '',
                        }}
                        render={({ handleSubmit: submitForm, form, submitting, pristine }) => (
                            <form onSubmit={submitForm}>
                                <Typography className={classes.sectionLabel}>
                                    <LocalOfferOutlinedIcon /> LABELS
                                </Typography>

                                <div className={classes.fieldsRow}>
                                    <div className={classes.fieldCol}>
                                        <Typography className={classes.fieldHeading}>SINGULAR</Typography>
                                        <Field name="singularLabel">
                                            {({ input, meta }) => (
                                                <TextField
                                                    {...input}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    error={meta.touched && Boolean(meta.error)}
                                                    helperText={meta.touched && meta.error ? meta.error : undefined}
                                                />
                                            )}
                                        </Field>
                                        <Typography className={classes.defaultHint}>
                                            Default: {item.defaultSingularLabel}
                                        </Typography>
                                    </div>

                                    <div className={classes.fieldCol}>
                                        <Typography className={classes.fieldHeading}>PLURAL</Typography>
                                        <Field name="pluralLabel">
                                            {({ input, meta }) => (
                                                <TextField
                                                    {...input}
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    error={meta.touched && Boolean(meta.error)}
                                                    helperText={meta.touched && meta.error ? meta.error : undefined}
                                                />
                                            )}
                                        </Field>
                                        <Typography className={classes.defaultHint}>
                                            Default: {item.defaultPluralLabel}
                                        </Typography>
                                    </div>
                                </div>

                                <Divider className={classes.divider} />

                                <Typography className={classes.sectionLabel}>
                                    <InfoOutlinedIcon /> HELPER TEXT
                                </Typography>

                                <Field name="helperText">
                                    {({ input }) => (
                                        <TextField
                                            {...input}
                                            fullWidth
                                            multiline
                                            minRows={2}
                                            variant="outlined"
                                        />
                                    )}
                                </Field>
                                <Typography className={classes.helperHint}>
                                    A short description shown with this field label.
                                </Typography>

                                <Divider className={classes.divider} />

                                <Typography className={classes.sectionLabel}>
                                    <InfoOutlinedIcon /> TOOLTIP
                                </Typography>

                                <Typography className={classes.fieldHeading}>TOOLTIP TEXT</Typography>
                                <Field name="tooltip">
                                    {({ input }) => (
                                        <TextField
                                            {...input}
                                            fullWidth
                                            multiline
                                            minRows={3}
                                            variant="outlined"
                                            placeholder="e.g. A high-value enquiry flagged as a key business opportunity…"
                                        />
                                    )}
                                </Field>
                                <Typography className={classes.helperHint}>
                                    Shown when users hover over this label in the UI. Leave blank to show no
                                    tooltip.
                                </Typography>

                                <Divider className={classes.divider} />

                                <div className={classes.actionsRow}>
                                    <Button
                                        type="submit"
                                        variant="outlined"
                                        className={classes.saveButton}
                                        startIcon={<CheckIcon />}
                                        disabled={submitting || pristine}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        startIcon={<RefreshIcon />}
                                        onClick={() => handleResetToDefault(form)}
                                    >
                                        Reset to default
                                    </Button>
                                </div>
                            </form>
                        )}
                    />
                </CardContent>
            </Collapse>
        </Card>
    );
}

/* -------------------------------------------------------------------------- */
/* FieldLabelsTab                                                             */
/* -------------------------------------------------------------------------- */

interface FieldLabelsTabProps {

}

export default function FieldLabelsTab(props: FieldLabelsTabProps) {
    
    const terminiLogyQuery = useQuery<ApplicationTerminologyData, ApplicationTerminologyParams>(ApplicationTerminoLogyQuery);

    // const [terminology, setTerminology] = useState<ApplicationTerminology[]>(items);


    const onUpdateTerminology = (labelKey: string, values: TerminologyFormValues): Promise<void> => {
        return client.mutate<UpdateApplicationTerminologyData, UpdateApplicationTerminologyParams>({
            mutation: UpdateApplicationTerminologyMutation,
            variables: {
                labelKey,
                singularLabel: values.singularLabel,
                pluralLabel: values.pluralLabel,
                helperText: values.helperText || null,
                tooltip: values.tooltip || null,
            },
        }).then((response) => {
            if (response.data?.updateApplicationTerminology) {
                updateApplicationTerminologyLocalStorage(labelKey, {
                    singularLabel: values.singularLabel,
                    pluralLabel: values.pluralLabel,
                    helperText: values.helperText || null,
                    tooltip: values.tooltip || null,
                });
                showNotification(null, 'Successfully submitted.', 'info'); 
                return;
            }
            throw new Error('The terminology update was not accepted.');
        }).catch((error) => {
            showNotification('Failed to update terminology.', error, 'error');
            throw error;
        });
    };

    const handleSave = (labelKey: string, values: TerminologyFormValues) => {
        return onUpdateTerminology(labelKey, values);
    };

    const handleResetToDefault = (labelKey: string) => {
        onUpdateTerminology(labelKey, {
            singularLabel: null,
            pluralLabel: null,
            helperText: null,
            tooltip: null,
        });
    };

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                Field Labels
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
                Customize the singular, plural, and tooltip text used for key labels across the application.
            </Typography>

            {terminiLogyQuery.data?.lookup.applicationTerminology.map((item) => (
                <TerminologyCard
                    key={item.labelKey}
                    item={item}
                    onSave={(values) => handleSave(item.labelKey, values)}
                    onResetToDefault={() => handleResetToDefault(item.labelKey)}
                />
            ))}
        </Box>
    );
}


export interface ApplicationTerminologyParams {
    labelKey?: string;
}

export interface ApplicationTerminologyData {
    lookup: Lookup;
}

export interface Lookup {
    applicationTerminology: ApplicationTerminology[];
}

export interface ApplicationTerminology {
    guid:                 string;
    labelKey:             string;
    singularLabel:        null | string;
    pluralLabel:          null | string;
    defaultPluralLabel:   string;
    defaultSingularLabel: string;
    helperText:           null | string;
    tooltip:              null | string;
    internalDisplayLabel: null | string;
}

export const ApplicationTerminoLogyQuery = gql`
    query ApplicationTerminology(
        $labelKey: String
    ) {
        lookup {
            applicationTerminology (labelKey: $labelKey) {
                guid
                labelKey
                singularLabel
                pluralLabel
                defaultPluralLabel
                defaultSingularLabel
                helperText
                tooltip
                internalDisplayLabel
            }
        }
    }
`;

interface UpdateApplicationTerminologyParams {
    labelKey: string | null;
    singularLabel: string | null;
    pluralLabel: string | null;
    helperText: string | null;
    tooltip: string | null;
}

interface UpdateApplicationTerminologyData {
    updateApplicationTerminology: boolean;
}

const UpdateApplicationTerminologyMutation = gql`
    mutation UpdateApplicationTerminology(
        $labelKey: String,
        $singularLabel: String,
        $pluralLabel: String,
        $helperText: String,
        $tooltip: String
    ) {
        updateApplicationTerminology (
            labelKey: $labelKey,
            singularLabel: $singularLabel,
            pluralLabel: $pluralLabel, 
            helperText: $helperText,
            tooltip: $tooltip
        )
    }
`;